import { apiService as api } from './api';

export interface UploadResponse {
  success: boolean;
  message: string;
  data?: {
    filename: string;
    original_name: string;
    path: string;
    url: string;
    size: number;
    mime_type: string;
  };
}

export interface FileListResponse {
  success: boolean;
  data: Array<{
    name: string;
    path: string;
    url: string;
    size: number;
    last_modified: number;
  }>;
}

/**
 * URL publica de um arquivo guardado, a partir do caminho.
 *
 * O que fica no banco e o caminho relativo, nao a URL: URL carrega o dominio
 * junto, e um lancamento salvo antes de o endereco mudar apontaria para sempre
 * ao endereco antigo. Montar na hora de exibir mantem o dado independente de
 * onde o sistema esta hospedado.
 */
export const urlDoArquivo = (caminho: string): string => {
  if (/^https?:\/\//i.test(caminho)) return caminho;
  return `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/storage/${caminho.replace(/^\/+/, '')}`;
};

export const uploadService = {
  // Upload de arquivo
  uploadFile: async (file: File, type: 'comprovante' | 'nota_fiscal' | 'documento'): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await api.post<UploadResponse>('/v1/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  },

  // Deletar arquivo
  deleteFile: async (path: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete<{ success: boolean; message: string }>('/v1/upload', {
      data: { path }
    });

    return response;
  },

  // Listar arquivos por tipo
  listFiles: async (type: 'comprovante' | 'nota_fiscal' | 'documento' = 'comprovante'): Promise<FileListResponse> => {
    const response = await api.get<FileListResponse>(`/v1/upload/list?type=${type}`);
    return response;
  },

  // Formatar tamanho do arquivo
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Validar tipo de arquivo
  validateFileType: (file: File): boolean => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    return allowedTypes.includes(file.type);
  },

  // Validar tamanho do arquivo (10MB max)
  validateFileSize: (file: File): boolean => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    return file.size <= maxSize;
  }
};
